import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import TextGeneratorReview from './components/TextGeneratorReview';
import ImageGeneratorReview from './components/ImageGeneratorReview';
import VideoCreatorReview from './components/VideoCreatorReview';
import { textGeneratorTool, imageGeneratorTool, videoCreatorTool } from '../../mocks/aiTools';

const tabs = [
  {
    key: 'text',
    label: textGeneratorTool.name,
    shortLabel: 'AI Text Generator',
    icon: 'ri-quill-pen-line',
    rating: textGeneratorTool.rating,
    category: textGeneratorTool.category,
  },
  {
    key: 'image',
    label: imageGeneratorTool.name,
    shortLabel: 'AI Image Generator',
    icon: 'ri-image-ai-line',
    rating: imageGeneratorTool.rating,
    category: imageGeneratorTool.category,
  },
  {
    key: 'video',
    label: videoCreatorTool.name,
    shortLabel: 'AI Video Creator',
    icon: 'ri-video-ai-line',
    rating: videoCreatorTool.rating,
    category: videoCreatorTool.category,
  },
];

export default function AIToolsPage() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'text');

  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab && tabs.some(t => t.key === tab)) {
      setActiveTab(tab);
    }
  }, [searchParams]);

  const handleTabChange = (key: string) => {
    setActiveTab(key);
    setSearchParams({ tab: key });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const activeToolData = tabs.find(t => t.key === activeTab);

  return (
    <div className="font-sans min-h-screen bg-brokenWhite-100">
      <Navbar />

      {/* Page Header */}
      <div className="pt-24 pb-6 bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <nav className="flex items-center gap-2 text-sm text-gray-400 mb-4">
            <Link to="/" className="hover:text-clairBlue-500 transition-colors">Home</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-darkBlue-500 font-medium">AI Tools Hub</span>
          </nav>
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <h1 className="font-display text-3xl font-bold text-darkBlue-500 mb-2">AI Tools Hub</h1>
              <p className="text-gray-500">Honest reviews, guides, and recommendations for the best AI tools available.</p>
            </div>
            <div className="bg-clairBlue-50 border border-clairBlue-100 rounded-xl px-4 py-3 text-sm text-clairBlue-600 flex items-center gap-2">
              <i className="ri-information-line"></i>
              <span>Some links are affiliate links. <strong>We only recommend tools we believe in.</strong></span>
            </div>
          </div>
        </div>
      </div>

      {/* Tool Selector Cards */}
      <div className="bg-white border-b border-gray-100 sticky top-20 z-40">
        <div className="max-w-7xl mx-auto px-4 lg:px-8 py-3">
          <div className="flex gap-3 overflow-x-auto scrollbar-hide">
            {tabs.map(tab => (
              <button
                key={tab.key}
                onClick={() => handleTabChange(tab.key)}
                className={`flex items-center gap-3 px-5 py-2.5 rounded-xl border-2 font-medium text-sm whitespace-nowrap transition-all cursor-pointer flex-shrink-0 ${
                  activeTab === tab.key
                    ? 'border-clairBlue-300 bg-clairBlue-50 text-clairBlue-600'
                    : 'border-gray-100 bg-white text-gray-500 hover:border-clairBlue-200 hover:bg-clairBlue-50'
                }`}
              >
                <i className={tab.icon}></i>
                <div className="text-left hidden md:block">
                  <div className="font-semibold">{tab.label}</div>
                  <div className="text-xs opacity-70">{tab.category}</div>
                </div>
                <span className="md:hidden">{tab.shortLabel}</span>
                <div className="flex items-center gap-0.5 ml-1">
                  <i className="ri-star-fill text-amber-400 text-xs"></i>
                  <span className="text-xs font-semibold">{tab.rating}</span>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 lg:px-8 py-10">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Main Review */}
          <main className="flex-1 min-w-0">
            {activeTab === 'text' && <TextGeneratorReview />}
            {activeTab === 'image' && <ImageGeneratorReview />}
            {activeTab === 'video' && <VideoCreatorReview />}
          </main>

          {/* Sidebar */}
          <aside className="w-full lg:w-72 flex-shrink-0 space-y-5">
            {/* Quick Compare */}
            <div className="bg-white rounded-2xl p-5 border border-gray-100 sticky top-44">
              <h3 className="font-semibold text-darkBlue-500 text-sm mb-4 flex items-center gap-2">
                <i className="ri-bar-chart-horizontal-line text-clairBlue-400"></i>
                All Tool Ratings
              </h3>
              <div className="space-y-4">
                {tabs.map(tab => (
                  <button
                    key={tab.key}
                    onClick={() => handleTabChange(tab.key)}
                    className={`w-full text-left p-3 rounded-xl border transition-all cursor-pointer ${activeTab === tab.key ? 'border-clairBlue-300 bg-clairBlue-50' : 'border-gray-100 hover:border-clairBlue-200'}`}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <i className={`${tab.icon} text-clairBlue-400 text-sm`}></i>
                      <span className="font-medium text-darkBlue-500 text-xs">{tab.label}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-0.5">
                        {[1,2,3,4,5].map(i => (
                          <i key={i} className={`text-xs ${i <= Math.floor(tab.rating) ? 'ri-star-fill text-amber-400' : 'ri-star-line text-gray-300'}`}></i>
                        ))}
                      </div>
                      <span className="text-darkBlue-500 font-bold text-xs">{tab.rating}/5</span>
                    </div>
                  </button>
                ))}
              </div>

              <div className="border-t border-gray-100 pt-4 mt-4">
                <p className="text-gray-400 text-xs mb-3">Need help choosing the right tool?</p>
                <Link
                  to="/?tab=contact"
                  onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
                  className="w-full flex items-center justify-center gap-2 bg-darkBlue-500 text-white text-xs font-semibold py-2.5 rounded-lg hover:bg-darkBlue-600 transition-colors"
                >
                  <i className="ri-customer-service-2-line"></i> Ask Our Experts
                </Link>
              </div>
            </div>

            {/* BientoMart Services Promo */}
            <div className="bg-gradient-to-br from-darkBlue-500 to-darkBlue-600 rounded-2xl p-5 text-white">
              <div className="w-10 h-10 flex items-center justify-center bg-clairBlue-300/20 rounded-xl mb-3">
                <i className="ri-rocket-2-line text-clairBlue-300"></i>
              </div>
              <h3 className="font-semibold text-sm mb-2">Want Expert Help?</h3>
              <p className="text-gray-400 text-xs leading-relaxed mb-4">
                Let our team set up and manage these AI tools for your business. Save time and get better results.
              </p>
              <Link
                to="/services"
                className="w-full flex items-center justify-center gap-2 bg-clairBlue-300 text-darkBlue-500 text-xs font-bold py-2.5 rounded-lg hover:bg-clairBlue-200 transition-colors"
              >
                <i className="ri-arrow-right-line"></i> Explore Our Services
              </Link>
            </div>
          </aside>
        </div>
      </div>

      <Footer />
    </div>
  );
}
